import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Brain, Search, RefreshCw, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCOPES = ['SESSION', 'DEAL', 'MERCHANT', 'DOCUMENT_TYPE', 'USER', 'COMPANY', 'GLOBAL'];
const SCOPE_REACH = { SESSION: 0, DEAL: 1, MERCHANT: 2, DOCUMENT_TYPE: 3, USER: 4, COMPANY: 5, GLOBAL: 6 };
const BROADEN = new Set(['USER', 'COMPANY', 'GLOBAL']);
const TYPES = ['TERMINOLOGY', 'USER_PREFERENCE', 'COMPANY_RULE', 'DOCUMENT_RULE', 'CORRECTION', 'DEAL_SPECIFIC', 'APPROVED_EXAMPLE'];
const SOURCES = ['USER_EXPLICIT', 'USER_CORRECTION', 'ADMIN_CREATED', 'APPROVED_EXAMPLE', 'SYSTEM_DERIVED'];
const ALL = '__all__';

const scopeColor = (s) => ({
  DEAL: 'bg-purple-100 text-purple-700', MERCHANT: 'bg-blue-100 text-blue-700',
  DOCUMENT_TYPE: 'bg-teal-100 text-teal-700', USER: 'bg-amber-100 text-amber-700',
  COMPANY: 'bg-slate-100 text-slate-700', GLOBAL: 'bg-red-100 text-red-700',
  SESSION: 'bg-gray-100 text-gray-500',
}[s] || 'bg-gray-100 text-gray-600');

const fmtDate = (v) => (v ? new Date(v).toLocaleString() : '—');
const scopeTarget = (m) => m.deal_id || m.merchant_id || m.document_type || m.user_id || null;
const scopeLabel = (m) => `${m.scope}${scopeTarget(m) ? ' · ' + scopeTarget(m) : ''}`;

const MilaMemory = () => {
  const { getAuthHeaders } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    active: ALL, memory_type: ALL, scope: ALL, source: ALL, promoted: ALL,
    deal_id: '', merchant_id: '', document_type: '', user_id: '', q: '',
  });
  const [detail, setDetail] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [correctOpen, setCorrectOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmForget, setConfirmForget] = useState(null);
  const [confirmPromote, setConfirmPromote] = useState(null);

  const headers = useCallback(() => getAuthHeaders(), [getAuthHeaders]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.active !== ALL) params.active = filters.active === 'active';
      if (filters.memory_type !== ALL) params.memory_type = filters.memory_type;
      if (filters.scope !== ALL) params.scope = filters.scope;
      if (filters.source !== ALL) params.source = filters.source;
      if (filters.promoted !== ALL) params.promoted = filters.promoted === 'promoted';
      ['deal_id', 'merchant_id', 'document_type', 'user_id', 'q'].forEach((k) => {
        if (filters[k]?.trim()) params[k] = filters[k].trim();
      });
      const res = await axios.get(`${API}/agent/memory`, { headers: headers(), params });
      setRows(res.data.memories || []);
    } catch (e) {
      toast.error('Failed to load memories');
    } finally {
      setLoading(false);
    }
  }, [filters, headers]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/agent/memory/${id}`, { headers: headers() });
      setDetail(res.data);
    } catch (e) { toast.error('Could not open memory'); }
  };
  const refreshDetail = async () => { if (detail?.memory?.id) await openDetail(detail.memory.id); };

  const act = async (fn, okMsg) => {
    try { await fn(); toast.success(okMsg); await load(); await refreshDetail(); return true; }
    catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Action failed'));
      return false;
    }
  };

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const activeCount = rows.filter((r) => r.active !== false).length;

  return (
    <Layout>
      <div className="p-6 max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Brain className="h-6 w-6 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold">MILA Memory</h1>
              <p className="text-sm text-gray-500">Inspect and manage everything MILA has learned. {rows.length} shown · {activeCount} active.</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-1" />Refresh</Button>
            <Button onClick={() => setAddOpen(true)}>Add memory</Button>
          </div>
        </div>

        <Card className="mb-4">
          <CardContent className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div className="col-span-2 lg:col-span-2">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2 top-2.5 text-gray-400" />
                <Input className="pl-8" placeholder="term or content…" value={filters.q}
                  onChange={(e) => setF('q', e.target.value)} />
              </div>
            </div>
            <FilterSelect label="Status" value={filters.active} onChange={(v) => setF('active', v)}
              options={[['active', 'Active'], ['inactive', 'Inactive']]} />
            <FilterSelect label="Scope" value={filters.scope} onChange={(v) => setF('scope', v)}
              options={SCOPES.map((s) => [s, s])} />
            <FilterSelect label="Type" value={filters.memory_type} onChange={(v) => setF('memory_type', v)}
              options={TYPES.map((s) => [s, s.replace(/_/g, ' ')])} />
            <FilterSelect label="Source" value={filters.source} onChange={(v) => setF('source', v)}
              options={SOURCES.map((s) => [s, s.replace(/_/g, ' ')])} />
            <FilterSelect label="Promoted" value={filters.promoted} onChange={(v) => setF('promoted', v)}
              options={[['promoted', 'Promoted'], ['unpromoted', 'Unpromoted']]} />
            <div><Label className="text-xs">Deal</Label><Input placeholder="10823" value={filters.deal_id} onChange={(e) => setF('deal_id', e.target.value)} /></div>
            <div><Label className="text-xs">Merchant</Label><Input placeholder="shortcode" value={filters.merchant_id} onChange={(e) => setF('merchant_id', e.target.value)} /></div>
            <div><Label className="text-xs">Doc type</Label><Input placeholder="payoff" value={filters.document_type} onChange={(e) => setF('document_type', e.target.value)} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Term</TableHead>
                  <TableHead>Content</TableHead>
                  <TableHead>Scope</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Uses</TableHead>
                  <TableHead>Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-400">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-400">No memories match these filters.</TableCell></TableRow>
                ) : rows.map((m) => (
                  <TableRow key={m.id} className="cursor-pointer hover:bg-gray-50" onClick={() => openDetail(m.id)}>
                    <TableCell className="font-medium">{m.term}</TableCell>
                    <TableCell className="max-w-[320px] truncate text-gray-600">{m.content || m.definition}</TableCell>
                    <TableCell>
                      <Badge className={scopeColor(m.scope)}>{scopeLabel(m)}</Badge>
                      {m.is_session && <span className={`ml-1 text-[10px] ${m.expired ? 'text-red-500' : 'text-gray-400'}`}>{m.expired ? 'expired' : 'temp'}</span>}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">{(m.memory_type || '').replace(/_/g, ' ')}</TableCell>
                    <TableCell className="text-xs">{m.source_label}</TableCell>
                    <TableCell>
                      {m.active === false
                        ? <Badge variant="outline" className="text-gray-400">inactive</Badge>
                        : <Badge className="bg-green-100 text-green-700">active</Badge>}
                      {m.promoted && m.active !== false && <span className="ml-1 text-[10px] text-blue-500">trusted</span>}
                    </TableCell>
                    <TableCell className="text-right text-gray-500">{m.usage_count || 0}</TableCell>
                    <TableCell className="text-xs text-gray-500">{fmtDate(m.updated_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {detail?.memory && (
            <DetailBody
              d={detail}
              onEdit={() => setEditOpen(true)}
              onScope={() => setScopeOpen(true)}
              onCorrect={() => setCorrectOpen(true)}
              onForget={() => setConfirmForget(detail.memory)}
              onPromote={() => setConfirmPromote(detail.memory)}
              onToggle={() => act(
                () => axios.post(`${API}/agent/memory/${detail.memory.id}/${detail.memory.active === false ? 'enable' : 'disable'}`, {}, { headers: headers() }),
                detail.memory.active === false ? 'Re-enabled' : 'Disabled')}
              onOpenRelated={openDetail}
            />
          )}
        </SheetContent>
      </Sheet>

      {detail?.memory && (
        <>
          <EditDialog open={editOpen} onOpenChange={setEditOpen} memory={detail.memory}
            onSave={(body) => act(() => axios.patch(`${API}/agent/memory/${detail.memory.id}`, body, { headers: headers() }), 'Saved').then((ok) => ok && setEditOpen(false))} />
          <ScopeDialog open={scopeOpen} onOpenChange={setScopeOpen} memory={detail.memory}
            onSave={(body) => act(() => axios.post(`${API}/agent/memory/${detail.memory.id}/scope`, body, { headers: headers() }), 'Scope changed').then((ok) => ok && setScopeOpen(false))} />
          <CorrectDialog open={correctOpen} onOpenChange={setCorrectOpen} memory={detail.memory}
            onSave={(content) => act(() => axios.post(`${API}/agent/memory/${detail.memory.id}/correct`, { content }, { headers: headers() }), 'Corrected (old version kept)').then((ok) => ok && setCorrectOpen(false))} />
        </>
      )}

      <AddDialog open={addOpen} onOpenChange={setAddOpen}
        onSave={(body) => act(() => axios.post(`${API}/agent/memory`, body, { headers: headers() }), 'Added').then((ok) => ok && setAddOpen(false))} />

      <AlertDialog open={!!confirmForget} onOpenChange={(o) => !o && setConfirmForget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Forget this memory?</AlertDialogTitle>
            <AlertDialogDescription>
              This marks “{confirmForget?.term}” inactive so MILA stops using it. History is preserved (soft-delete) — it can be re-enabled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const id = confirmForget.id; setConfirmForget(null); act(() => axios.post(`${API}/agent/memory/${id}/disable`, {}, { headers: headers() }), 'Forgotten (inactive)'); }}>
              Forget
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmPromote} onOpenChange={(o) => !o && setConfirmPromote(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Promote to durable memory?</AlertDialogTitle>
            <AlertDialogDescription>
              “{confirmPromote?.term}” is a temporary session memory. Promoting makes it a permanent COMPANY memory that applies in all future sessions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const id = confirmPromote.id; setConfirmPromote(null); act(() => axios.post(`${API}/agent/memory/${id}/promote`, { scope: 'COMPANY', confirm: true }, { headers: headers() }), 'Promoted to COMPANY'); }}>
              Promote
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

const FilterSelect = ({ label, value, onChange, options }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const Field = ({ label, children }) => (
  children ? <div className="mb-3"><div className="text-xs uppercase text-gray-400">{label}</div><div className="text-sm">{children}</div></div> : null
);

const DetailBody = ({ d, onEdit, onScope, onCorrect, onForget, onPromote, onToggle, onOpenRelated }) => {
  const m = d.memory;
  const AUDIT_LABEL = {
    memory_created: 'Created', memory_reinforced: 'Reinforced', memory_edited: 'Edited',
    memory_updated: 'Updated', memory_superseded: 'Superseded', memory_disabled: 'Disabled',
    memory_enabled: 'Re-enabled', memory_scope_changed: 'Scope changed', memory_promoted: 'Promoted',
  };
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2 flex-wrap">
          {m.term}
          <Badge className={scopeColor(m.scope)}>{scopeLabel(m)}</Badge>
          {m.is_session && <Badge variant="outline" className={m.expired ? 'text-red-500' : 'text-gray-500'}>{m.expired ? 'session · expired' : 'session · temporary'}</Badge>}
        </SheetTitle>
      </SheetHeader>

      <div className="mt-4">
        <Field label="Content">{m.content || m.definition}</Field>
        <div className="grid grid-cols-2 gap-x-4">
          <Field label="Memory type">{(m.memory_type || '').replace(/_/g, ' ')}</Field>
          <Field label="Source">{m.source_label} <span className="text-gray-400">({m.source})</span></Field>
          <Field label="Deal">{m.deal_id}</Field>
          <Field label="Merchant">{m.merchant_id}</Field>
          <Field label="Document type">{m.document_type}</Field>
          <Field label="User">{m.user_id}</Field>
          <Field label="Importance">{m.importance}</Field>
          <Field label="Confidence">{m.confidence_score != null ? Math.round(m.confidence_score) : null}</Field>
          <Field label="Status">{m.active === false ? 'Inactive' : 'Active'}{m.promoted ? ' · trusted' : ''}</Field>
          <Field label="Usage">{m.usage_count || 0} uses</Field>
          <Field label="Created">{fmtDate(m.created_at)}</Field>
          <Field label="Updated">{fmtDate(m.updated_at)}</Field>
          <Field label="Last used">{fmtDate(m.last_used_at)}</Field>
          {m.is_session && <Field label="Session">{m.session_id}</Field>}
        </div>

        {(d.supersedes || d.superseded_by) && (
          <div className="mt-2 mb-3 p-3 rounded bg-amber-50 border border-amber-200 text-sm">
            <div className="font-medium mb-1 flex items-center gap-1"><AlertTriangle className="h-4 w-4 text-amber-500" />Supersede history</div>
            {d.supersedes && (
              <div className="cursor-pointer hover:underline" onClick={() => onOpenRelated(d.supersedes.id)}>
                ↳ Replaced older: “{d.supersedes.content || d.supersedes.definition}”
              </div>)}
            {d.superseded_by && (
              <div className="cursor-pointer hover:underline" onClick={() => onOpenRelated(d.superseded_by.id)}>
                ↳ Replaced by newer: “{d.superseded_by.content || d.superseded_by.definition}”
              </div>)}
          </div>
        )}

        <div className="flex flex-wrap gap-2 my-4">
          {m.is_session
            ? <Button size="sm" onClick={onPromote}>Promote to durable</Button>
            : <>
                <Button size="sm" variant="outline" onClick={onEdit}>Edit</Button>
                <Button size="sm" variant="outline" onClick={onCorrect}>Correct / Replace</Button>
                <Button size="sm" variant="outline" onClick={onScope}>Change scope</Button>
              </>}
          <Button size="sm" variant="outline" onClick={onToggle}>{m.active === false ? 'Re-enable' : 'Disable'}</Button>
          {m.active !== false && <Button size="sm" variant="destructive" onClick={onForget}>Forget</Button>}
        </div>

        <div className="mt-2">
          <div className="text-xs uppercase text-gray-400 mb-1">Audit trail</div>
          {(d.audit || []).length === 0 ? <div className="text-sm text-gray-400">No events recorded.</div> : (
            <div className="space-y-1">
              {d.audit.map((a) => (
                <div key={a.id} className="text-xs flex justify-between border-b py-1">
                  <span>{AUDIT_LABEL[a.event] || a.event}{a.actor ? ` · ${a.actor}` : ''}</span>
                  <span className="text-gray-400">{fmtDate(a.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const EditDialog = ({ open, onOpenChange, memory, onSave }) => {
  const [content, setContent] = useState('');
  const [importance, setImportance] = useState('3');
  useEffect(() => { if (open) { setContent(memory.content || memory.definition || ''); setImportance(String(memory.importance || 3)); } }, [open, memory]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit memory</DialogTitle></DialogHeader>
        <Label>Content</Label>
        <Textarea rows={4} value={content} onChange={(e) => setContent(e.target.value)} />
        <Label>Importance (1–5)</Label>
        <Input type="number" min={1} max={5} value={importance} onChange={(e) => setImportance(e.target.value)} />
        <p className="text-xs text-gray-400">Live dollar balances can’t be stored — save the rule, not the figure.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave({ content, importance: Number(importance) })}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CorrectDialog = ({ open, onOpenChange, memory, onSave }) => {
  const [content, setContent] = useState('');
  useEffect(() => { if (open) setContent(''); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Correct / Replace</DialogTitle></DialogHeader>
        <div className="text-sm text-gray-500 mb-1">Current: “{memory.content || memory.definition}”</div>
        <Label>New content (the old version is kept and marked superseded)</Label>
        <Textarea rows={4} value={content} onChange={(e) => setContent(e.target.value)} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(content)}>Replace</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ScopeDialog = ({ open, onOpenChange, memory, onSave }) => {
  const [scope, setScope] = useState(memory.scope);
  const [target, setTarget] = useState('');
  const [confirmBroaden, setConfirmBroaden] = useState(false);
  useEffect(() => { if (open) { setScope(memory.scope); setTarget(''); setConfirmBroaden(false); } }, [open, memory]);
  const broadening = SCOPE_REACH[scope] > SCOPE_REACH[memory.scope];
  const needsConfirm = broadening && BROADEN.has(scope);
  const needsTarget = ['DEAL', 'MERCHANT', 'DOCUMENT_TYPE'].includes(scope);
  const build = () => {
    const body = { scope, confirm_broaden: confirmBroaden };
    if (scope === 'DEAL') body.deal_id = target;
    if (scope === 'MERCHANT') body.merchant_id = target;
    if (scope === 'DOCUMENT_TYPE') body.document_type = target;
    return body;
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Change scope</DialogTitle></DialogHeader>
        <div className="text-sm">Current scope: <Badge className={scopeColor(memory.scope)}>{scopeLabel(memory)}</Badge></div>
        <Label>New scope</Label>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{SCOPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        {needsTarget && (
          <>
            <Label>{scope === 'DEAL' ? 'Deal number' : scope === 'MERCHANT' ? 'Merchant shortcode' : 'Document type'}</Label>
            <Input value={target} onChange={(e) => setTarget(e.target.value)} />
          </>
        )}
        {needsConfirm && (
          <div className="p-3 rounded bg-red-50 border border-red-200 text-sm">
            <div className="flex items-center gap-1 font-medium text-red-600"><AlertTriangle className="h-4 w-4" />Broadening scope</div>
            <p className="text-red-600 my-1">{memory.scope} → {scope} makes this rule apply far more widely (potentially every deal). Confirm to proceed.</p>
            <label className="flex items-center gap-2"><input type="checkbox" checked={confirmBroaden} onChange={(e) => setConfirmBroaden(e.target.checked)} />I understand and want to broaden this memory.</label>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={(needsConfirm && !confirmBroaden) || (needsTarget && !target.trim())} onClick={() => onSave(build())}>
            {needsConfirm ? 'Broaden & Save' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddDialog = ({ open, onOpenChange, onSave }) => {
  const [f, setF] = useState({ term: '', definition: '', scope: 'COMPANY', deal_id: '', merchant_id: '', document_type: '', importance: '3' });
  useEffect(() => { if (open) setF({ term: '', definition: '', scope: 'COMPANY', deal_id: '', merchant_id: '', document_type: '', importance: '3' }); }, [open]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const build = () => {
    const b = { term: f.term, definition: f.definition, scope: f.scope, importance: Number(f.importance) };
    if (f.scope === 'DEAL') b.deal_id = f.deal_id;
    if (f.scope === 'MERCHANT') b.merchant_id = f.merchant_id;
    if (f.scope === 'DOCUMENT_TYPE') b.document_type = f.document_type;
    return b;
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add memory</DialogTitle></DialogHeader>
        <Label>Term</Label>
        <Input value={f.term} onChange={(e) => set('term', e.target.value)} placeholder="e.g. PIF" />
        <Label>Definition / rule</Label>
        <Textarea rows={3} value={f.definition} onChange={(e) => set('definition', e.target.value)} placeholder="what it means / what to do" />
        <Label>Scope</Label>
        <Select value={f.scope} onValueChange={(v) => set('scope', v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{SCOPES.filter((s) => s !== 'SESSION').map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        {f.scope === 'DEAL' && <><Label>Deal number</Label><Input value={f.deal_id} onChange={(e) => set('deal_id', e.target.value)} /></>}
        {f.scope === 'MERCHANT' && <><Label>Merchant shortcode</Label><Input value={f.merchant_id} onChange={(e) => set('merchant_id', e.target.value)} /></>}
        {f.scope === 'DOCUMENT_TYPE' && <><Label>Document type</Label><Input value={f.document_type} onChange={(e) => set('document_type', e.target.value)} /></>}
        <p className="text-xs text-gray-400">Live dollar balances can’t be stored — save the rule, not the figure.</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!f.term.trim() || !f.definition.trim()} onClick={() => onSave(build())}>Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MilaMemory;
